/**
 * 选择操作处理 Hook
 */
import React, { useCallback } from "react";
import { RoleNode, PermissionTransferItem } from "../config/types";
import { Admin } from "../config/types";
import { createRoleNode } from "../utils/roleUtils";

export interface UseSelectionHandlersProps {
  selectedRole: RoleNode | null;
  selectedAdmin: Admin | null;
  setSelectedRole: (role: RoleNode | null) => void;
  setSelectedAdmin: (admin: Admin | null) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  fetchRolePermissionsAndAll: (roleId: string) => Promise<void>;
  fetchAdminPermissionsAndAll: (adminId: string) => Promise<void>;
  onSavePermissions: (roleId: string, selectedPermissions?: PermissionTransferItem[]) => Promise<void>;
  onRemovePermission: (permissionId: string, roleId: string) => void;
  updateRolePermissionCount: (roleId: string, count: number) => void;
  handleSaveAdminPermissions: (adminId: string, extraPermissions: PermissionTransferItem[]) => Promise<void>;
  adminRolePermissions: string[];
  permissionTreeRef: React.RefObject<any>;
  rolePermissionDataSourceRef: React.RefObject<PermissionTransferItem[]>;
  adminPermissionDataSourceRef: React.RefObject<PermissionTransferItem[]>;
}

export interface UseSelectionHandlersReturn {
  handleSelectRole: (item: any) => void;
  handleSelectAdmin: (admin: Admin) => void;
  handleSavePermissions: () => Promise<void>;
  handleRemovePermission: (permissionId: string) => void;
  handlePermissionChangeCallback: (selectedKeys: string[] | PermissionTransferItem[]) => void;
}

/**
 * 选择操作处理 Hook
 */
export const useSelectionHandlers = ({
  selectedRole,
  selectedAdmin,
  setSelectedRole,
  setSelectedAdmin,
  setHasUnsavedChanges,
  fetchRolePermissionsAndAll,
  fetchAdminPermissionsAndAll,
  onSavePermissions,
  onRemovePermission,
  updateRolePermissionCount,
  handleSaveAdminPermissions,
  adminRolePermissions,
  permissionTreeRef,
  rolePermissionDataSourceRef,
  adminPermissionDataSourceRef,
}: UseSelectionHandlersProps): UseSelectionHandlersReturn => {
  const handleSelectRole = useCallback(
    (item: any) => {
      if (!item || !item.id) {
        return;
      }

      const roleNode = createRoleNode(item);
      setSelectedRole(roleNode);
      setSelectedAdmin(null); // 清除管理员选择
      setHasUnsavedChanges(false);
      fetchRolePermissionsAndAll(roleNode.id);
    },
    [setSelectedRole, setSelectedAdmin, setHasUnsavedChanges, fetchRolePermissionsAndAll]
  );

  const handleSelectAdmin = useCallback(
    (admin: Admin) => {
      console.log("📋 选择管理员:", admin);
      setSelectedAdmin(admin);
      setSelectedRole(null); // 清除角色选择
      setHasUnsavedChanges(false);
      // 加载管理员的权限数据（角色权限 + 额外权限）
      fetchAdminPermissionsAndAll(admin.id);
    },
    [setSelectedAdmin, setSelectedRole, setHasUnsavedChanges, fetchAdminPermissionsAndAll]
  );

  const handleSavePermissions = useCallback(async () => {
    // 判断是保存角色权限还是管理员权限
    if (selectedRole) {
      // 保存角色权限
      try {
        console.log("💾 ========== 开始保存角色权限 ==========");
        console.log("💾 角色ID:", selectedRole.id);
        console.log("💾 角色名称:", selectedRole.leftContent);

        // 从当前数据源收集已勾选的权限（不依赖组件ref）
        const collectSelected = (nodes: PermissionTransferItem[]): PermissionTransferItem[] => {
          const result: PermissionTransferItem[] = [];
          const dfs = (arr: PermissionTransferItem[]) => {
            for (const n of arr) {
              if (n.type !== "MODULE" && n.isAssigned) {
                result.push(n);
              }
              if (Array.isArray(n.children) && n.children.length > 0) {
                dfs(n.children);
              }
            }
          };
          dfs(nodes);
          return result;
        };
        const current = rolePermissionDataSourceRef.current || [];
        const selectedPermissions = collectSelected(current);
          console.log("💾 从Tree获取到的选中权限:", selectedPermissions);
          console.log("💾 权限数量:", selectedPermissions.length);
          console.log(
            "💾 权限详情:",
            selectedPermissions.map((p) => ({
              id: p.id,
              title: p.title,
              type: p.type,
            }))
          );

          if (selectedPermissions.length === 0) {
            console.warn("⚠️ 没有选中任何权限，取消保存");
            return;
          }

          // 调用保存API，传递选中的权限
          console.log("💾 调用保存API...");
          await onSavePermissions(selectedRole.id, selectedPermissions);
          setHasUnsavedChanges(false);

          // 更新角色权限数量
          console.log("💾 更新角色权限数量...");
          await updateRolePermissionCount(selectedRole.id, selectedPermissions.length);

          // 重新获取角色权限数据，以更新permissionTree的isAssigned属性
          console.log("🔄 重新获取角色权限数据以刷新显示...");
          await fetchRolePermissionsAndAll(selectedRole.id);

          console.log("✅ ========== 角色权限保存完成 ==========");
      } catch (error) {
        console.error("❌ 保存角色权限失败:", error);
      }
    } else if (selectedAdmin) {
      // 保存管理员额外权限
      try {
        console.log("💾 ========== 开始保存管理员额外权限 ==========");
        console.log("💾 管理员ID:", selectedAdmin.id);
        console.log("💾 管理员名称:", selectedAdmin.name);

        // 从当前数据源收集已勾选的权限
        const collectSelected = (nodes: PermissionTransferItem[]): PermissionTransferItem[] => {
          const result: PermissionTransferItem[] = [];
          const dfs = (arr: PermissionTransferItem[]) => {
            for (const n of arr) {
              if (n.type !== "MODULE" && n.isAssigned) {
                result.push(n);
              }
              if (Array.isArray(n.children) && n.children.length > 0) {
                dfs(n.children);
              }
            }
          };
          dfs(nodes);
          return result;
        };
        const current = adminPermissionDataSourceRef.current || [];
        const selectedPermissions = collectSelected(current);
          console.log("💾 从Tree获取到的选中权限:", selectedPermissions);

          // 过滤掉来自角色的权限，只保存额外权限
          const extraPermissions = selectedPermissions.filter((p) => !adminRolePermissions.includes(p.id));
          console.log("💾 过滤后的额外权限:", extraPermissions);
          console.log("💾 额外权限数量:", extraPermissions.length);

          // 调用保存API，只传递额外权限
          console.log("💾 调用保存API...");
          await handleSaveAdminPermissions(selectedAdmin.id, extraPermissions);
          setHasUnsavedChanges(false);

          // 重新获取管理员权限数据
          console.log("🔄 重新获取管理员权限数据以刷新显示...");
          await fetchAdminPermissionsAndAll(selectedAdmin.id);

          console.log("✅ ========== 管理员额外权限保存完成 ==========");
      } catch (error) {
        console.error("❌ 保存管理员额外权限失败:", error);
      }
    } else {
      console.error("❌ 未选择角色或管理员");
    }
  }, [
    selectedRole,
    selectedAdmin,
    onSavePermissions,
    updateRolePermissionCount,
    fetchRolePermissionsAndAll,
    handleSaveAdminPermissions,
    fetchAdminPermissionsAndAll,
    adminRolePermissions,
    permissionTreeRef,
    rolePermissionDataSourceRef,
    adminPermissionDataSourceRef,
    setHasUnsavedChanges,
  ]);

  const handleRemovePermission = useCallback(
    (permissionId: string) => {
      if (!selectedRole) return;
      onRemovePermission(permissionId, selectedRole.id);
      setHasUnsavedChanges(true);
    },
    [selectedRole, onRemovePermission, setHasUnsavedChanges]
  );

  const handlePermissionChangeCallback = useCallback(
    (selectedKeys: string[] | PermissionTransferItem[]) => {
      if (!selectedRole) return;
      // 不立即更新rolePermissions，只标记有未保存的更改
      setHasUnsavedChanges(true);
      // 可以在这里添加一个临时状态来存储选中的权限
      console.log("权限选择变化:", selectedKeys.length, "个权限");
    },
    [selectedRole, setHasUnsavedChanges]
  );

  return {
    handleSelectRole,
    handleSelectAdmin,
    handleSavePermissions,
    handleRemovePermission,
    handlePermissionChangeCallback,
  };
};

